import moment from 'moment';
import { Box, Column, Divider, ProfessionalHeader, Text } from '~/commonComponents';
import { ProfessionalBestInDentalWinnerTooltip } from '~/components/BestInDental';
import { BestInDentalStatus } from '~/enums/BestInDental';
import { useCandidate } from '~/hooks/useCandidate';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import ProfessionalBestInDentalWinnerRibbon from '~/images/best-in-dental-professional-ribbon-v2.png';
import { IJob, IUser } from '~/models';
import { Colors } from '~/themes/colors';
import { EducationUtils } from '~/utils/EducationUtils';
import { ProfessionalInfoSection } from './components/ProfileInfoSection';
import {
  DnDaProcedures,
  ProfessionalAttestationInfo,
  RdhFdProcedures,
} from '~/components/CandidateOverview/components';

interface IProfileCardProps {
  professional: IUser;
  job: IJob | null;
}

export const ProfileCard = ({ professional, job }: IProfileCardProps) => {
  const user = useSelectedOffice(job?.offer_owner?.id);
  const {
    specialtyMatch,
    insuranceMismatch,
    userSpecialties,
    softwares,
    licenseNumber,
    licenseState,
    issueDate,
    nationalProviderIdentifier,
    shouldShowNpi,
    professionName,
    candidateInsurances,
    yearsOfExperience,
    candidateOffer,
  } = useCandidate(user, professional, job);

  const isBestInDentalWinner =
    professional?.professional_info?.bestInDental?.status === BestInDentalStatus.Winner;

  return (
    <Box
      position="relative"
      paddingTop={24}
      paddingBottom={32}
      paddingHorizontal={32}
      backgroundColor={Colors.white}
      style={{
        borderRadius: 8,
        boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
      }}
    >
      {isBestInDentalWinner && (
        <ProfessionalBestInDentalWinnerTooltip
          Anchor={
            <img
              alt="Best In Dental Winner Ribbon"
              src={ProfessionalBestInDentalWinnerRibbon}
              style={{ width: 184, height: 24 }}
            />
          }
          style={{ container: { position: 'absolute', top: 0, right: 20 } }}
        />
      )}

      <ProfessionalHeader professional={professional} />

      <Divider height={1} />

      <Column gap={20}>
        <ProfessionalAttestationInfo consent={candidateOffer?.consent} />

        <ProfessionalInfoSection
          title="Education"
          value={
            <Text>
              {professional?.license?.school}
              <br />
              Graduation Date: {moment(professional?.license?.graduationDate).format('MM/DD/YYYY')}
            </Text>
          }
        />
        <ProfessionalInfoSection title="Profession" value={professionName} />
        <Column gap={4}>
          <ProfessionalInfoSection title="License #" isSameLine value={licenseNumber || '-'} />
          <ProfessionalInfoSection
            title="Issued by State of"
            isSameLine
            value={licenseState || '-'}
          />
          <ProfessionalInfoSection
            title="Year Issued"
            isSameLine
            value={issueDate ? moment(issueDate).format('YYYY') : '-'}
          />
        </Column>

        {nationalProviderIdentifier && shouldShowNpi && (
          <ProfessionalInfoSection title="NPI" value={nationalProviderIdentifier} />
        )}

        <ProfessionalInfoSection title="Years of Experience" value={yearsOfExperience} />

        <ProfessionalInfoSection
          title="Software"
          value={softwares?.join(', ')}
          showWarningIcon={job ? !specialtyMatch || !!insuranceMismatch : false}
        />

        {shouldShowNpi && (
          <ProfessionalInfoSection
            title="Insurance"
            value={candidateInsurances?.join(', ')}
            showWarningIcon={job ? !!insuranceMismatch : false}
          />
        )}

        <ProfessionalInfoSection
          title="Specialty(ies)"
          value={EducationUtils.mapToRemoveSpecialtyPrefix(userSpecialties).join(', ')}
          showWarningIcon={job ? !specialtyMatch || !!insuranceMismatch : false}
        />

        <Box style={{ width: '100%' }}>
          <Text bold fontSize={20}>
            Experienced Procedures:
          </Text>
          <RdhFdProcedures professional={professional} job={job} />
          <DnDaProcedures professional={professional} job={job} />
        </Box>
      </Column>
    </Box>
  );
};
